import { HttpStatusCode } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ExternalTransferApiService } from '@api/external-transfer';
import { ApiErrorModel } from '@api/http';
import {
	ApiConnectionsModel,
	ApiConnectionsRepository,
	WorkspaceIdGeneratorService,
	WorkspaceRepository
} from '@database/central';
import { ModalController, NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { regexPatternForConnectionUrl, ToastDuration, ToastService } from '@shared';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ConnectionsModalComponentsEnum, ConnectionTypeEnum } from '../../models';
import { ConnectionsModalState } from '../../services';

@Component({
	selector: 'app-connections-add-edit',
	templateUrl: 'add-edit.component.html',
	styleUrls: ['add-edit.component.scss']
})
export class ConnectionsAddEditComponent implements OnInit, OnDestroy {
	@Input() public connectionType: ConnectionTypeEnum;
	@Input() public connectionId: string | null;

	public displayBackButton$: Observable<boolean> = this.workfileRepository.hasWorkfilesImported$();
	public aliasControl: UntypedFormControl;
	public apiKeyControl: UntypedFormControl;
	public pathControl: UntypedFormControl;
	public connectionForm: UntypedFormGroup;
	public formValidationMessages = [];
	private unsubscriber$: Subject<boolean> = new Subject<boolean>();

	public constructor(
		private navController: NavController,
		private translate: TranslateService,
		private apiConnectionsRepository: ApiConnectionsRepository,
		private workspaceIdGeneratorService: WorkspaceIdGeneratorService,
		private workfileRepository: WorkspaceRepository,
		private toastService: ToastService,
		private connectionsModalState: ConnectionsModalState,
		private modalCtrl: ModalController,
		private externalTransferApiService: ExternalTransferApiService
	) {}

	public ngOnInit(): void {
		this.aliasControl = new UntypedFormControl('', [Validators.required]);
		this.apiKeyControl = new UntypedFormControl('');
		this.pathControl = new UntypedFormControl('', [
			Validators.required,
			// Regex allows only valid URL or domain (http/s://domain.extension | domain.extension | xxx.xxx.xxx.xxx | xxx.xxx.xxx.xxx:00)
			Validators.pattern(regexPatternForConnectionUrl())
		]);

		this.connectionForm = new UntypedFormGroup({
			aliasControl: this.aliasControl,
			apiKeyControl: this.apiKeyControl,
			pathControl: this.pathControl
		});

		this.formValidationMessages = [
			{
				type: 'pattern',
				message: this.translate.instant('connectionsModal.form.pathManually.validations.pattern')
			},
			{
				type: 'apiUrl',
				message: this.translate.instant('api.errorMessages.noServerAvailability')
			},
			{
				type: 'noInternet',
				message: this.translate.instant('api.errorMessages.noInternetConnection')
			},
			{
				type: 'loading',
				message: this.translate.instant("Please wait...")
			}
		];

		if (this.connectionId) {
			this.apiConnectionsRepository
				.getConnectionDetails$(this.connectionId)
				.pipe(takeUntil(this.unsubscriber$))
				.subscribe((connectionDetails: ApiConnectionsModel | null) => {
					if (connectionDetails) {
						this.aliasControl.setValue(connectionDetails.serverAlias);
						this.apiKeyControl.setValue(connectionDetails.apiKey);
						this.pathControl.setValue(connectionDetails.serverPath);
					}
				});
		}
	}

	public goBack(): void {
		this.navController.pop();
	}

	public async onSubmit(formValues: {
		aliasControl: string;
		apiKeyControl: string;
		pathControl: string;
	}): Promise<void> {
		if (!this.isFormValid()) {
			return;
		}
		this.pathControl.setErrors({ loading: true })

		this.externalTransferApiService
		.pingApi(this.pathControl.value)
		.pipe(takeUntil(this.unsubscriber$))
		.subscribe(
			() => {
				this.pathControl.setErrors(null);
				this.confirmConnection(formValues);
			},
			(error: ApiErrorModel) => {
				if (error.httpStatusCode === HttpStatusCode.ServiceUnavailable) {
					this.pathControl.setErrors({ noInternet: true });
					return
				}

				if (error.httpStatusCode === HttpStatusCode.NotFound) {
					this.pathControl.setErrors({ apiUrl: true });
					return
				}

				this.pathControl.setErrors(null);
			}
		);
	}

	public async confirmConnection(formValues: {
		aliasControl: string;
		apiKeyControl: string;
		pathControl: string;
	}) {
		formValues.pathControl = formValues.pathControl.replace(/\/+$/, '');

		console.log(formValues.pathControl);
		
		if (this.connectionId) {
			try {
				await this.apiConnectionsRepository.updateConnectionAliasAndPath(
					this.connectionId,
					formValues.aliasControl,
					formValues.apiKeyControl,
					formValues.pathControl
				);

				this.connectionsModalState.setActive({
					componentName: ConnectionsModalComponentsEnum.editOptionsConnection,
					connectionId: this.connectionId
				});
			} catch (error) {
				await this.toastService.presentError(
					this.translate.instant('connectionsModal.form.pathManually.errors.save'),
					ToastDuration.Slow
				);
			}
			return;
		}

		const apiConnectionId = this.workspaceIdGeneratorService.generateRandomId();

		try {
			await this.apiConnectionsRepository.insert({
				connectionId: apiConnectionId,
				serverPath: formValues.pathControl,
				serverAlias: formValues.aliasControl,
				apiKey: formValues.apiKeyControl
			});

			if (this.connectionType === ConnectionTypeEnum.includeOptions) {
				this.connectionsModalState.setActive({
					componentName: ConnectionsModalComponentsEnum.optionsConnection,
					connectionId: apiConnectionId
				});
			} else {
				this.modalCtrl.dismiss(apiConnectionId);
			}
		} catch (error) {
			await this.toastService.presentError(
				this.translate.instant('connectionsModal.form.pathManually.errors.save'),
				ToastDuration.Slow
			);
		}
	}

	public isFormValid(): boolean {
		return this.connectionForm.valid;
	}

	public ngOnDestroy(): void {
		this.unsubscriber$.next(true);
		this.unsubscriber$.unsubscribe();
	}

	public fieldValueChanged(e: any): void {
		if (e.detail.value && !this.pathControl.errors) {
			this.pathControl.setErrors(null);
		}
	}

	private testAPIConnection() {
		this.pathControl.setErrors({ apiUrl: true });

		this.externalTransferApiService
			.pingApi(this.pathControl.value)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				() => this.pathControl.setErrors(null),
				(error: ApiErrorModel) => {
					if (error.httpStatusCode === HttpStatusCode.ServiceUnavailable) {
						this.pathControl.setErrors({ noInternet: true });
						return;
					}

					if (error.httpStatusCode === HttpStatusCode.NotFound) {
						this.pathControl.setErrors({ apiUrl: true });
						return ;
					}

					this.pathControl.setErrors(null);
				}
			);
	}
}
